import { DomainList, DomainListItemCard } from "@/components/domain";
import { useDeleteTour, useGetTours } from "@/hooks/tours";
import { T } from "@/i18n";
import { Tour } from "@/models";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Building2, Waypoints } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/(tours)/tours")({
  component: RouteComponent,
});

function RouteComponent() {
  const query = useGetTours();

  return (
    <DomainList
      domain="tours"
      breadcrumbs={[
        {
          content: (
            <>
              <T tKey="tours.title" /> <Waypoints width={16} height={16} />
            </>
          ),
          href: "/tours",
        },
      ]}
      tableOptions={{
        hasActions: true,
        columns: [
          {
            header: "tours.fields.name",
          },
          {
            header: "tours.fields.category",
          },
          {
            header: "tours.fields.type",
          },
        ],
      }}
      type="table"
      data={query.data}
      isLoading={query.isLoading}
      newLink="/tours/new"
      renderItem={(tours, index, isTable) => (
        <TourCard tour={tours} key={index} isTable={isTable} />
      )}
    />
  );
}

function TourCard({ tour, isTable }: { tour: Tour; isTable: boolean }) {
  const { mutateAsync } = useDeleteTour();
  const deleteTour = () => mutateAsync(tour.id.toString());
  const { t } = useTranslation();
  return (
    <DomainListItemCard
      domain="tours"
      table={isTable}
      title={tour.name}
      viewLink={(children, key) => (
        <Link key={key} to="/tours/$id" params={{ id: tour.id.toString() }}>
          {children}
        </Link>
      )}
      editLink={(children, key) => (
        <Link
          key={key}
          to="/tours/$id/edit"
          params={{ id: tour.id.toString() }}
        >
          {children}
        </Link>
      )}
      extraItems={[
        {
          label: "tourAgencies.tour.title",
          icon: () => <Building2 />,
          itemWrapper: ({ children, key }) => (
            <Link
              key={key}
              to="/tour-agencies/$id"
              search={{ domain: "tour" }}
              params={{ id: tour.id.toString() }}
            >
              {children}
            </Link>
          ),
        },
        {
          label: "tourDays.title",
          icon: () => <Waypoints />,
          itemWrapper: ({ children, key }) => (
            <Link
              key={key}
              to="/tour-days/$id"
              params={{ id: tour.id.toString() }}
            >
              {children}
            </Link>
          ),
        },
      ]}
      contentMap={[
        tour.category
          ? {
              label: t("tours.fields.category"),
              value: <T tKey={`tours.categories.${tour.category}`}></T>,
            }
          : null,
        tour.type
          ? {
              label: t("tours.fields.type"),
              value: <T tKey={`tours.types.${tour.type}`}></T>,
            }
          : null,
      ]}
      onDelete={deleteTour}
    />
  );
}
